import type { EatalyBreadcrumbItem } from '@/components/eataly-breadcrumbs';
import type { PlpPathnameCategoryMatchSource } from '@/lib/plp/resolve-category-for-pathname';
import type { ShopperProducts } from '@/scapi';
import { buildEatalyCategoryBreadcrumbItems } from '@/lib/breadcrumb/build-eataly-breadcrumb-items';

export type BuildOptimisticPlpBreadcrumbParams = {
    pendingPathname: string;
    currentPathname: string;
    resolvedTarget?: {
        node: ShopperProducts.schemas['Category'];
        source: PlpPathnameCategoryMatchSource;
    };
};

/**
 * Builds a breadcrumb trail from the **current** route `category` loader payload and the pending URL,
 * without extra API calls. Used while PLP navigation is in flight.
 */
export function buildOptimisticPlpBreadcrumbItems(
    routeCategory: ShopperProducts.schemas['Category'],
    params: BuildOptimisticPlpBreadcrumbParams
): EatalyBreadcrumbItem[] | undefined {
    const { pendingPathname, currentPathname, resolvedTarget } = params;

    if (pendingPathname === currentPathname) {
        return undefined;
    }

    if (!resolvedTarget?.node?.id) {
        return undefined;
    }

    const targetCategory = categoryContextForPendingPlp(routeCategory, resolvedTarget.node, resolvedTarget.source);
    return buildEatalyCategoryBreadcrumbItems(targetCategory);
}

/**
 * Shapes `parentCategoryTree` for the pending PLP target (`resolveCategoryNodeForPlpPathname`):
 * `ancestor` truncates the tree; `directChild` extends with the route category; `route` keeps the full tree.
 */
function categoryContextForPendingPlp(
    routeCategory: ShopperProducts.schemas['Category'],
    targetNode: ShopperProducts.schemas['Category'],
    source: PlpPathnameCategoryMatchSource
): ShopperProducts.schemas['Category'] {
    const tree = routeCategory.parentCategoryTree ?? [];
    const targetId = targetNode.id;
    if (source === 'ancestor') {
        const targetIndex = tree.findIndex((node) => node.id === targetId);
        if (targetIndex >= 0) {
            return {
                ...targetNode,
                parentCategoryTree: tree.slice(0, targetIndex + 1),
            };
        }
    }

    if (source === 'directChild') {
        const routeIndex = tree.findIndex((node) => node.id === routeCategory.id);
        const parentTree =
            routeIndex >= 0
                ? tree.slice(0, routeIndex + 1)
                : [
                      ...tree,
                      {
                          id: routeCategory.id,
                          name: routeCategory.name,
                          c_url: routeCategory.c_url,
                      },
                  ];

        return {
            ...targetNode,
            parentCategoryTree: parentTree,
        };
    }

    return {
        ...targetNode,
        parentCategoryTree: tree,
    };
}
